import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Play, Pause, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatEta } from "@/lib/utils";

export const CrawlProgress = ({ stats, onRefresh }) => {
  const percent = stats.percent_complete || 0;
  const crawled = stats.crawled || 0;
  const total = stats.total || 0;
  const rate = stats.crawl_rate_per_hour || 0;
  const eta = stats.eta_hours;
  const isActive = stats.crawler_active;
  const isPaused = stats.crawler_paused;

  const handleControl = async (action) => {
    try {
      await api.post("/crawl/control", { action });
      toast.success(`Crawler ${action}ed`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} crawler`);
    }
  };

  // CMS Breakdown bar
  const cms = stats.cms_breakdown || {};
  const cmsTotal = Object.values(cms).reduce((a, b) => a + b, 0) || 1;
  const cmsColors = {
    Drupal: "bg-blue-500",
    WordPress: "bg-emerald-500",
    TYPO3: "bg-purple-500",
    Contao: "bg-amber-500",
    Shopify: "bg-pink-500",
    Joomla: "bg-orange-500",
    Magento: "bg-red-500",
    PrestaShop: "bg-fuchsia-500",
    Wix: "bg-yellow-500",
    Squarespace: "bg-slate-400",
    Webflow: "bg-indigo-500",
    Ghost: "bg-violet-500",
    Hugo: "bg-rose-500",
    Gatsby: "bg-purple-400",
    WooCommerce: "bg-violet-600",
    OpenCart: "bg-sky-500",
    BigCommerce: "bg-blue-400",
    "Salesforce Commerce Cloud": "bg-cyan-500",
    "Adobe Experience Manager": "bg-red-600",
    Sitecore: "bg-red-400",
    Kentico: "bg-orange-400",
    Umbraco: "bg-blue-600",
    "HubSpot CMS": "bg-orange-500",
    Jimdo: "bg-lime-500",
    IONOS: "bg-blue-500",
    Strato: "bg-sky-400",
    Laravel: "bg-red-500",
    "Next.js": "bg-zinc-300",
    "Nuxt.js": "bg-emerald-600",
    Angular: "bg-red-600",
    "Vue.js": "bg-emerald-500",
    unknown: "bg-zinc-600",
    undetected: "bg-zinc-700",
  };

  return (
    <div className="stat-card rounded-sm p-3 sm:p-4 space-y-3 sm:space-y-4" data-testid="crawl-progress">
      {/* Top row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-bold tracking-tight text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Crawl Progress
          </h3>
          {isActive && !isPaused && (
            <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[10px]">
              <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Active
            </Badge>
          )}
          {isActive && isPaused && (
            <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 text-[10px]">Paused</Badge>
          )}
          {!isActive && (
            <Badge className="bg-zinc-500/15 text-zinc-400 border-zinc-500/30 text-[10px]">Idle</Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!isActive && (
            <Button
              data-testid="crawl-start-btn"
              size="sm"
              onClick={() => handleControl("start")}
              className="h-7 bg-blue-600 hover:bg-blue-500 text-xs rounded-sm"
            >
              <Play className="w-3 h-3 mr-1" /> Start
            </Button>
          )}
          {isActive && !isPaused && (
            <Button
              data-testid="crawl-pause-btn"
              size="sm"
              variant="outline"
              onClick={() => handleControl("pause")}
              className="h-7 text-xs rounded-sm border-zinc-700 text-zinc-300"
            >
              <Pause className="w-3 h-3 mr-1" /> Pause
            </Button>
          )}
          {isActive && isPaused && (
            <Button
              data-testid="crawl-resume-btn"
              size="sm"
              onClick={() => handleControl("resume")}
              className="h-7 bg-blue-600 hover:bg-blue-500 text-xs rounded-sm"
            >
              <Play className="w-3 h-3 mr-1" /> Resume
            </Button>
          )}
          {isActive && (
            <Button
              data-testid="crawl-stop-btn"
              size="sm"
              variant="outline"
              onClick={() => handleControl("stop")}
              className="h-7 text-xs rounded-sm border-zinc-700 text-zinc-300"
            >
              <Square className="w-3 h-3 mr-1" /> Stop
            </Button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex justify-between text-[11px] text-zinc-500 mb-1.5 font-mono">
          <span>{crawled.toLocaleString()} / {total.toLocaleString()} crawled</span>
          <span>{percent.toFixed(2)}%</span>
        </div>
        <div className="h-2 bg-zinc-800 rounded-sm overflow-hidden">
          <div
            className={`h-full bg-blue-600 rounded-sm ${isActive && !isPaused ? 'progress-striped' : ''}`}
            style={{ width: `${Math.min(percent, 100)}%`, transition: 'width 0.3s ease-out' }}
          />
        </div>
        <div className="flex gap-4 mt-2 text-[11px] text-zinc-500 font-mono">
          <span>Rate: {rate.toLocaleString()}/hr</span>
          {eta != null && <span>ETA: {formatEta(eta)}</span>}
          <span>Workers: {stats.pace_workers ?? 0}{stats.pace_processes > 1 ? ` · ${stats.pace_processes} processes` : ""}</span>
        </div>
      </div>

      {/* CMS Breakdown */}
      {Object.keys(cms).length > 0 && (
        <div>
          <p className="text-[11px] font-medium tracking-wide uppercase text-zinc-500 mb-2">CMS Breakdown</p>
          <div className="h-3 rounded-sm overflow-hidden flex">
            {Object.entries(cms).sort((a, b) => b[1] - a[1]).map(([name, count]) => (
              <div
                key={name}
                className={`${cmsColors[name] || 'bg-zinc-600'} h-full`}
                style={{ width: `${(count / cmsTotal) * 100}%` }}
                title={`${name}: ${count.toLocaleString()}`}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
            {Object.entries(cms).sort((a, b) => b[1] - a[1]).map(([name, count]) => (
              <span key={name} className="text-[11px] text-zinc-400 font-mono flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-sm ${cmsColors[name] || 'bg-zinc-600'}`} />
                {name}: {count.toLocaleString()} ({((count / cmsTotal) * 100).toFixed(1)}%)
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
